import { useEffect, useState } from "react";
import { SetupProvider, useSetup } from "./SetupContext";
import type { PipelineStep, SubStep, WizardProps } from "./steps/wizardTypes";
import { PipelineStepper7, SubStepTabs, SummarySidebar } from "./steps/PipelineStepper7";

import { SubStep1 } from "./steps/SubStep1";
import { SubStep2 } from "./steps/SubStep2";
import { SubStep3 } from "./steps/SubStep3";
import { SubStep4 } from "./steps/SubStep4";

import { DataIngestionStep } from "./steps/DataIngestionStep";
import { DataCleaningStep } from "./steps/DataCleaningStep";

import { SupplierConsolidationStep } from "./steps/SupplierConsolidationStep";
import { SupplierClassificationStep } from "./steps/SupplierClassificationStep";
import { PublishDashboardStep } from "./steps/PublishDashboardStep";
import { GovernanceQAStep } from "./steps/GovernanceQAStep";

type Props = WizardProps;

type WizardLocation = {
  pipelineStep: PipelineStep;
  subStep: SubStep;
};

type PageHeadingContent = {
  breadcrumbRoot: string;
  breadcrumbCurrent: string;
  pageHeaderTitle: string;
  pageHeaderDescription: string;
};

const getSetupPath = (pipelineStep: PipelineStep, subStep: SubStep) => {
  if (pipelineStep === 1) {
    const subPaths: Record<SubStep, string> = {
      1: "project-details",
      2: "taxonomy",
      3: "workflow",
      4: "users-access",
    };
    return `/setup/${subPaths[subStep]}`;
  }

  const pipelinePaths: Record<Exclude<PipelineStep, 1>, string> = {
    2: "data-ingestion",
    3: "data-cleaning",
    4: "supplier-consolidation",
    5: "supplier-classification",
    6: "publish-dashboard",
    7: "governance-qa",
  };

  return `/setup/${pipelinePaths[pipelineStep as Exclude<PipelineStep, 1>]}`;
};

const getWizardLocationFromPath = (pathname: string): WizardLocation => {
  const slug = pathname.replace(/^\/setup\/?/, "").trim();

  switch (slug) {
    case "taxonomy":
      return { pipelineStep: 1, subStep: 2 };
    case "workflow":
      return { pipelineStep: 1, subStep: 3 };
    case "users-access":
      return { pipelineStep: 1, subStep: 4 };
    case "data-ingestion":
      return { pipelineStep: 2, subStep: 4 };
    case "data-cleaning":
      return { pipelineStep: 3, subStep: 4 };
    case "supplier-consolidation":
      return { pipelineStep: 4, subStep: 4 };
    case "supplier-classification":
      return { pipelineStep: 5, subStep: 4 };
    case "publish-dashboard":
      return { pipelineStep: 6, subStep: 4 };
    case "governance-qa":
      return { pipelineStep: 7, subStep: 4 };
    case "project-details":
    default:
      return { pipelineStep: 1, subStep: 1 };
  }
};

const getPageHeadingContent = (pathname: string): PageHeadingContent => {
  const slug = pathname.replace(/^\/setup\/?/, "").trim();

  switch (slug) {
    case "project-details":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "Project Details",
        pageHeaderTitle: "New Project Setup",
        pageHeaderDescription: "Stand up a new Spend Intelligence engagement.",
      };
    case "taxonomy":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "Taxonomy",
        pageHeaderTitle: "New Project Setup",
        pageHeaderDescription: "Stand up a new Spend Intelligence engagement.",
      };
    case "workflow":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "Workflow",
        pageHeaderTitle: "New Project Setup",
        pageHeaderDescription: "Stand up a new Spend Intelligence engagement.",
      };
    case "users-access":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "Users & Access",
        pageHeaderTitle: "New Project Setup",
        pageHeaderDescription: "Stand up a new Spend Intelligence engagement.",
      };
    case "data-ingestion":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "DATA INGESTION",
        pageHeaderTitle: "Procurement Data Control Tower",
        pageHeaderDescription: "Connect ERPs, drop spreadsheet, and orchestrate ingestion pipelines for the engagement.",
      };
    case "data-cleaning":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "DATA CLEANING",
        pageHeaderTitle: "AI- assisted Procurement Data Operations",
        pageHeaderDescription: "Cleaning agent has processed 91%  of 4.2m records. Review remaining exceptions below.",
      };
    case "supplier-consolidation":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "DATA CLEANING",
        pageHeaderTitle: "AI- assisted Procurement Data Operations",
        pageHeaderDescription: "Cleaning agent has processed 91%  of 4.2m records. Review remaining exceptions below.",
      };
    case "supplier-classification":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "SPEND CLASSIFICATION",
        pageHeaderTitle: "AI- assisted Taxonomy Intelligence",
        pageHeaderDescription: "Classification agent has mapped suppliers and materials to UNSPSC v25.1. Review low-confidence rows below.",
      };
    case "publish-dashboard":
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "PUBLISH",
        pageHeaderTitle: "PUBLISH DASHBOARD",
        pageHeaderDescription: "Classification agent has mapped suppliers and materials to UNSPSC v25.1. Review low-confidence rows below.",
      };
    case "governance-qa":
      return {
        breadcrumbRoot: "PROJECT SETUP",
        breadcrumbCurrent: "GOVERNNACE",
        pageHeaderTitle: "Enterprise AI Governance Center",
        pageHeaderDescription:
          "Confidence monitoring, classification accuracy, human override tracking, and audit logs across the workflow.",
      };
    default:
      return {
        breadcrumbRoot: "Project Setup",
        breadcrumbCurrent: "Project Details",
        pageHeaderTitle: "New Project Setup",
        pageHeaderDescription: "Stand up a new Spend Intelligence engagement.",
      };
  }
};

/* ────────────────────────────────────────────────────────────── */
/*  WIZARD SHELL                                                  */
/* ────────────────────────────────────────────────────────────── */
function WizardShell({ onLaunch, onCancel, startFromDataIngestion = false }: Props & { onCancel: () => void }) {
  const [pipelineStep, setPipelineStep] = useState<PipelineStep>(() => getWizardLocationFromPath(window.location.pathname).pipelineStep);
  const [subStep, setSubStep]           = useState<SubStep>(() => getWizardLocationFromPath(window.location.pathname).subStep);
  const { completion } = useSetup();
  const [cleanedData, setCleanedData] = useState<any[]>([]);
  const [rawUploadData, setRawUploadData] = useState<any[]>([]);
  const [consolidationData, setConsolidationData] = useState<any>(null);
  const [classificationData, setClassificationData] = useState<any>(null);

  useEffect(() => {
    const handlePopState = () => {
      const location = getWizardLocationFromPath(window.location.pathname);
      setPipelineStep(location.pipelineStep);
      setSubStep(location.subStep);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const nextPath = getSetupPath(pipelineStep, subStep);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  }, [pipelineStep, subStep]);

  /* Navigation helpers */
  const nextSub = () => {
    if (subStep < 4) setSubStep((s) => (s + 1) as SubStep);
    else setPipelineStep(2);
  };

  const backSub = () => {
    if (subStep > 1) setSubStep((s) => (s - 1) as SubStep);
  };

  const nextPipeline = () =>
    setPipelineStep((s) => Math.min(7, s + 1) as PipelineStep);

  const backPipeline = () => {
    if (pipelineStep === 2) {
      setPipelineStep(1);
      setSubStep(4);
    } else {
      setPipelineStep((s) => Math.max(1, s - 1) as PipelineStep);
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? Your progress will not be saved."
      )
    ) {
      onCancel();
    }
  };

  const showSidebar = pipelineStep === 1;
  const { breadcrumbRoot, breadcrumbCurrent, pageHeaderTitle, pageHeaderDescription } =
    getPageHeadingContent(window.location.pathname);

  return (
    <>
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <span>{breadcrumbRoot}</span>
        <span className="breadcrumb__sep" aria-hidden="true">·</span>
        <span className="breadcrumb__current">{breadcrumbCurrent}</span>
      </nav>

      <div className="page-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h2 className="page-header__title">{pageHeaderTitle}</h2>
          <p className="page-header__description">{pageHeaderDescription}</p>
        </div>
      </div>

      <PipelineStepper7
        active={pipelineStep}
        totalSubStep={subStep}
        startFromDataIngestion={startFromDataIngestion}
      />

      <div className="project-info-wrapper">

        {pipelineStep === 1 && <SubStepTabs active={subStep} />}

        <div className={showSidebar ? "setup-layout bg-white" : ""}>
          <div style={{ flex: 1 }}>

            {/* ✅ STEP 1 */}
            {pipelineStep === 1 && subStep === 1 && (
              <SubStep1 onNext={nextSub} onCancel={handleCancel} />
            )}
            {pipelineStep === 1 && subStep === 2 && (
              <SubStep2 onNext={nextSub} onBack={backSub} onCancel={handleCancel} />
            )}
            {pipelineStep === 1 && subStep === 3 && (
              <SubStep3 onNext={nextSub} onBack={backSub} onCancel={handleCancel} />
            )}
            {pipelineStep === 1 && subStep === 4 && (
              <SubStep4 onNext={nextSub} onBack={backSub} onCancel={handleCancel} />
            )}

            {/* ✅ ✅ ✅ STEP 2 — DATA INGESTION */}
            {pipelineStep === 2 && (
              <DataIngestionStep
                onNext={(data, rawInput) => {
                  console.log("✅ DATA FROM INGESTION:", data);

                  // ✅ SAVE DATA FIRST
                  setCleanedData(data);
                  setRawUploadData(rawInput || []);

                  // ✅ DELAY STEP SWITCH (IMPORTANT FIX)
                  setTimeout(() => {
                    setPipelineStep(3);
                  }, 0);
                }}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

            {/* ✅ ✅ ✅ STEP 3 — DATA CLEANING */}
            {pipelineStep === 3 && cleanedData.length > 0 && (
              <DataCleaningStep
                key={cleanedData.length}   // ✅ FORCE RE-RENDER FIX
                data={cleanedData}         // ✅ CORRECT PROP
                originalData={rawUploadData}
                onNext={(data) => {
                  console.log("✅ DATA FROM SIMPLAI:", data);
                  setConsolidationData(data);
                  setPipelineStep(4);
                }}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

            {/* ✅ STEP 4 */}
            {pipelineStep === 4 && (
              <SupplierConsolidationStep
                data={consolidationData}
                onNext={(data) => {
                  setClassificationData(data);
                  setPipelineStep(5);
                }}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

            {/* ✅ STEP 5 */}
            {pipelineStep === 5 && (
              <SupplierClassificationStep
                data={classificationData}
                onNext={nextPipeline}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

            {/* ✅ STEP 6 */}
            {pipelineStep === 6 && (
              <PublishDashboardStep
                onNext={nextPipeline}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

            {/* ✅ STEP 7 */}
            {pipelineStep === 7 && (
              <GovernanceQAStep
                onLaunch={onLaunch}
                onBack={backPipeline}
                onCancel={handleCancel}
              />
            )}

          </div>

          {showSidebar && <SummarySidebar />}
        </div>

      </div>
    </>
  );
}

/* ✅ EXPORT */
export function SetupWizard({ onLaunch, onCancel, startFromDataIngestion = false }: Props) {
  return (
    <SetupProvider>
      <WizardShell
        onLaunch={onLaunch}
        onCancel={onCancel ?? onLaunch}
        startFromDataIngestion={startFromDataIngestion}
      />
    </SetupProvider>
  );
}